import moment from 'moment';
import Utils from '../../../../client/helpers/Utils';
import { findAddonPeriodOverlaps } from '../../../../client/helpers/addonPeriod';
import { isReadOnlyGrant, rowsForOverlapCheck } from '../../../../client/helpers/grantRows';

class ProductValidationService {

	static isSerialNumberValid = (serialNumber) => {
		const regExSerialNumberEndUser = /SY[0-9]{10}$/;
		const regExSerialNumberVMWare = /\VMware-/;
		if (serialNumber === null || serialNumber === undefined) {
			return false;
		} else {
			return regExSerialNumberEndUser.test(serialNumber) || regExSerialNumberVMWare.test(serialNumber);
		}
	};

	static hasSerialNumber(product) {
		return (product.virtualAppliance && product.activationCode || !product.virtualAppliance && product.serialNumber);
	}

	static isDurationValid = (maxMaintenanceExtension, totalDuration) => {
		return totalDuration <= maxMaintenanceExtension;

	};

	static isPositiveWholeNumber = (value) => {
		return /^\d+$/.test(String(value).trim()) && Number(value) > 0;
	};

	static isMaintenancePlanCurrentlySelling = (maintenancePlanId, maintenancePlansOptions) => {
		if (!maintenancePlansOptions || maintenancePlansOptions.length === 0) {
			return true;
		}
		const plan = maintenancePlansOptions.find((option) => { return option.id === maintenancePlanId; });
		if (!plan) {
			return true;
		}

		return plan.currentlySelling === true;
	};

	static isSelectActivationDateInFuture = (values) => {
		let hasWarnings = false;
		let today = moment();
		let activationDate = values && moment(values.activationDate, 'DD/MM/YYYY');

		if (activationDate > today) {
			hasWarnings = true;
		}

		return hasWarnings;
	};

	static validateProductModel = (product) => {
		return !!product?.modelId;
	};

	static validateSelectOptionsRenewal = (value) => {
		let hasErrors = false;
		if (!value || !value.length) {
			hasErrors = true;
		}

		return hasErrors;
	};

	static validateSelectOptionsDuration = (values) => {
		let hasErrors = false;

		if (values && values.maintenance) {
			if (!values.maintenance.duration || values.maintenance.duration === 'Select a duration') {
				hasErrors = true;
			}
		}

		return hasErrors;
	};

	static validateMaintenanceServices = (product, maintenancePlansOptions) => {
		let errors = [];

		if (product?.maintenanceServices) {
			errors = product.maintenanceServices.map((maintenanceService) => {
				let error = {};

				if (!maintenanceService.maintenancePlanId) {
					error.maintenancePlanId = 'Cannot be empty';
				} else if (!maintenanceService.id && !ProductValidationService.isMaintenancePlanCurrentlySelling(maintenanceService.maintenancePlanId, maintenancePlansOptions)) {
					error.maintenancePlanId = 'This support plan is no longer sold. Please select a currently selling one.';
				}

				if (!maintenanceService.duration) {
					error.duration = 'Cannot be empty';
				} else if (!ProductValidationService.isPositiveWholeNumber(maintenanceService.duration)) {
					error.duration = 'Must be a positive whole number';
				}

				if (!maintenanceService.quoteNumber) {
					error.quoteNumber = 'Cannot be empty';
				}

				if (!ProductValidationService.validateSaleValue(maintenanceService.saleValue)) {
					error.saleValue = 'Should look like this:\n' + '4000  1456.43  1456,43 or 1.456,43';
				}

				return error;
			});
		}

		return errors;
	};

	/*
	 * `licenseCatalog` is the license list (`GET /licensing/addons`): rows of an `editions` license may overlap, the
	 * older licenses keep refusing it. Rows Central made itself (temporary, trial, follows maintenance) are shown
	 * read-only and are neither validated nor counted in the overlap check.
	 */
	static validateAddons = (product, licenseCatalog = []) => {
		let errors = [];

		if (product && product.productAddons) {
			errors = product.productAddons.map((addon) => {
				let error = {};

				if (isReadOnlyGrant(addon)) {
					return error;
				}

				if (!addon.addonId) {
					error.addonId = 'Cannot be empty';
				}

				if (!addon.startDate && !addon.startOnActivation) {
					error.startDate = 'Cannot be empty';
				}

				if (!addon.duration) {
					error.duration = 'Cannot be empty';
				} else if (!ProductValidationService.isPositiveWholeNumber(addon.duration)) {
					error.duration = 'Must be a positive whole number';
				}

				return error;
			});

			ProductValidationService.applyAddonOverlapErrors(rowsForOverlapCheck(product.productAddons, licenseCatalog), errors);
		}

		return errors;
	};

	// A hidden row (isRenewed && isExpired, see ProductForm.js) has no rendered field to
	// attach an error to. Blocking Save on it would leave the operator stuck with no
	// visible explanation, so a conflict where both sides are hidden is left unblocked.
	static isAddonRowHidden = (addon) => {
		return !!(addon.isRenewed && addon.isExpired);
	};

	// Mutates `errors` in place, attaching an overlap error to the startDate of the
	// offending, visible row for every conflicting pair found by findAddonPeriodOverlaps.
	static applyAddonOverlapErrors = (productAddons, errors) => {
		const overlaps = findAddonPeriodOverlaps(productAddons);
		// When 3+ periods of the same add-on conflict, the same row can be `target` more than
		// once; keep the first message assigned to a row rather than letting a later pair
		// overwrite it with a possibly less useful one.
		const rowsWithMessage = new Set();

		overlaps.forEach(({ a, b }) => {
			const hiddenA = ProductValidationService.isAddonRowHidden(a.addon);
			const hiddenB = ProductValidationService.isAddonRowHidden(b.addon);

			if (hiddenA && hiddenB) {
				return;
			}

			let target = b;
			let other = a;

			if (hiddenA !== hiddenB) {
				target = hiddenA ? b : a;
				other = hiddenA ? a : b;
			} else {
				// Calendar dates stored at UTC midnight; parse in UTC so the comparison
				// does not depend on the browser's local timezone.
				const startA = a.addon.startDate ? moment.utc(a.addon.startDate) : null;
				const startB = b.addon.startDate ? moment.utc(b.addon.startDate) : null;

				if (startA && startB && startA.isAfter(startB)) {
					target = a;
					other = b;
				}
			}

			if (rowsWithMessage.has(target.index)) {
				return;
			}

			// Per the backend contract, two startOnActivation periods for the same add-on always
			// overlap, but that row's startDate field is disabled (see ProductAddon.js), so the
			// operator cannot act on a date-based remedy. Name the actual fix instead.
			if (target.period.startOnActivation && other.period.startOnActivation) {
				errors[target.index].startDate = 'Only one period for this license can start on activation. Uncheck "Start on activation" on one of them.';
				rowsWithMessage.add(target.index);
				return;
			}

			// `other.period.endDate` is already a moment instance (or null) - see addonPeriod.js.
			const otherEndDate = other.period.endDate;

			errors[target.index].startDate = otherEndDate
				? `Overlaps another period for this license (ends ${otherEndDate.format('DD/MM/YYYY')}). Start on ${otherEndDate.clone().add(1, 'day').format('DD/MM/YYYY')} or later.`
				: 'Overlaps another period for this license.';
			rowsWithMessage.add(target.index);
		});
	};

	static validateEndUser = (product) => {
		return product.virtualAppliance || !product.activationDate || !!product?.userId;
	};

	static validateActivationDate = (product) => {
		let regExp = new RegExp(/^[0-9]{2}[\/]{1}[0-9]{2}[\/]{1}[0-9]{4}$/);
		let activationDate = product?.activationDate;
		
		if (!regExp.test(activationDate)) {
			return false;
		}

		return true;
	};

	static validateNotesField(product) {
		return !product.virtualAppliance || !!product.notes;
	}

	static validateSaleValue = (saleValue) => {
		let normSaleValue = Utils.normalizeSaleValue(saleValue);
		let regExp = new RegExp(/^(\d+)(\.\d{1,2})?$/);

		if (saleValue === undefined || saleValue === null || saleValue === '') {
			return true;
		}

		if (!regExp.test(normSaleValue)) {
			return false;
		}

		return true;
	};

	static warn = (product) => {
		let warnings = {};

		if (ProductValidationService.isSelectActivationDateInFuture(product)) {
			warnings.activationDate = 'Selected date is in the future';
		}

		return warnings;
	};

	static validate = (product, props) => {
		const maintenancePlansOptions = props?.maintenancePlansOptions || [];
		let errors = {
			modelId: !ProductValidationService.validateProductModel(product) ? 'Cannot be empty' : null,
			notes: !ProductValidationService.validateNotesField(product) ? 'Cannot be empty' : null,
			saleValue: !ProductValidationService.validateSaleValue(product?.saleValue) ? 'Should look like this:\n' + '4000  1456.43  1456,43 or 1.456,43' : null,
			productAddons: ProductValidationService.validateAddons(product, props?.licenseCatalog || []),
			maintenanceServices: ProductValidationService.validateMaintenanceServices(product, maintenancePlansOptions),
			serialNumber: !ProductValidationService.hasSerialNumber(product) ? 'Cannot be empty' : null,
		};

		return errors;
	};
}

export default ProductValidationService;
